import { PlatformInfo } from './platformUtils';

export interface FileImportResult {
  success: boolean;
  content?: string;
  fileName?: string;
  error?: string;
}

export const importFile = (platform: PlatformInfo): Promise<FileImportResult> => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.txt,application/json,text/plain';
    
    // Mobile-specific attributes
    if (platform.isMobile) {
      input.setAttribute('capture', 'environment');
      input.multiple = false;
    }

    input.onchange = async (event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (!file) {
        resolve({ success: false, error: 'No file selected' });
        return;
      }

      // Check file type
      const validTypes = ['application/json', 'text/plain', 'text/json'];
      const validExtensions = ['.json', '.txt'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        resolve({ 
          success: false, 
          error: 'Invalid file type. Please select a JSON or text file.' 
        });
        return;
      }

      // Check file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        resolve({ 
          success: false, 
          error: 'File too large. Maximum size is 10MB.' 
        });
        return;
      }

      try {
        const content = await readFileContent(file);
        resolve({ 
          success: true, 
          content, 
          fileName: file.name 
        });
      } catch (error) {
        resolve({ 
          success: false, 
          error: error instanceof Error ? error.message : 'Failed to read file' 
        });
      }
    };

    input.onerror = () => {
      resolve({ success: false, error: 'Failed to open file picker' });
    };

    input.oncancel = () => {
      resolve({ success: false, error: 'File selection cancelled' });
    };

    // Trigger file picker
    input.click();
  });
};

export const readFileAsText = (file: File): Promise<{ success: boolean; content?: string; error?: string }> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const content = event.target?.result as string;
      resolve({ success: true, content });
    };
    
    reader.onerror = () => {
      resolve({ success: false, error: reader.error?.message || 'File read failed' });
    };
    
    reader.readAsText(file);
  });
};

const readFileContent = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const content = event.target?.result as string;
      resolve(content);
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file content'));
    };
    
    reader.readAsText(file);
  });
};

export const validateFileType = (file: File, allowedTypes: string[]): { isValid: boolean; error?: string } => {
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.') + 1);
  const mimeTypeMap: { [key: string]: string[] } = {
    json: ['application/json', 'text/json'],
    txt: ['text/plain'],
    csv: ['text/csv']
  };

  // Check by extension
  if (allowedTypes.includes(fileExtension)) {
    return { isValid: true };
  }

  // Check by MIME type
  for (const type of allowedTypes) {
    const mimeTypes = mimeTypeMap[type] || [];
    if (mimeTypes.includes(file.type)) {
      return { isValid: true };
    }
  }

  return { 
    isValid: false, 
    error: `Invalid file type. Allowed types: ${allowedTypes.join(', ')}` 
  };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 0) return '0 B';
  
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  
  if (bytes === 0) return '0 B';
  if (bytes < k) return Math.round(bytes) + ' B';
  
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), units.length - 1);
  const size = bytes / Math.pow(k, i);
  const roundedDownSize = Math.floor(size * 10) / 10;
  
  return roundedDownSize.toFixed(1) + ' ' + units[i];
};

export const validateAndParseImportedJSON = (content: string): { 
  isValid: boolean; 
  parsed?: any; 
  error?: string; 
} => {
  try {
    // Remove BOM if present
    const cleanContent = content.replace(/^\uFEFF/, '');
    
    // Try to parse as JSON
    const parsed = JSON.parse(cleanContent);
    return { isValid: true, parsed };
  } catch (error) {
    return { 
      isValid: false, 
      error: error instanceof Error ? error.message : 'Invalid JSON format' 
    };
  }
};

export const exportToFile = (content: string, fileName: string, platform: PlatformInfo): void => {
  try {
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    if (platform.isMobile) {
      // On mobile, try to use the Web Share API if available
      if (navigator.share && navigator.canShare?.({ files: [new File([blob], fileName)] })) {
        const file = new File([blob], fileName, { type: 'application/json' });
        navigator.share({
          title: 'JSON Path Finder Export',
          files: [file]
        }).catch(() => {
          // Fallback to download
          downloadFile(url, fileName);
        });
        return;
      }
    }
    
    // Standard download
    downloadFile(url, fileName);
    
    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (error) {
    console.error('Failed to export file:', error);
  }
};

const downloadFile = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};